import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { Tooltip } from './Tooltip';

/**
 * The refresh control that sits on every data table (AC-11).
 *
 * It exists because the alternative people were using is F5: this app's numbers are
 * scanner snapshots, so "is that still true?" is the most common question a screen
 * raises, and a full reload answers it by throwing away every filter, every open
 * section and the scroll position — on a page whose whole job is a list somebody has
 * just narrowed down.
 *
 * So the contract is deliberately narrow: it re-runs the queries BEHIND THIS TABLE and
 * nothing else. No full-page reload, no router navigation, no global refetch — Apollo
 * writes the new rows into the same components and React re-renders them in place, so
 * local state (a filter box, a selection, an expanded row) is untouched by
 * construction.
 *
 * `busy` is owned here rather than read off `query.loading`: with `cache-and-network`
 * a refetch of an already-cached query can resolve without ever flipping `loading` on
 * some paths, and a button that never shows it was pressed is a button people press
 * twice. The press is also the only thing that can start it, so a background poll can
 * never make the control look busy.
 *
 * There is ONE shape (owner decision e-12): a 26px icon-only glyph in the table or
 * section toolbar, with the word carried as the tooltip and the accessible name, and a
 * spin while it runs. A text variant existed for two pages and was removed in pass 7 —
 * two of thirty-six controls looking different is the inconsistency, not the ink.
 *
 * ONE PER QUERY, not one per table (owner decision e-22): sibling tables fed by the
 * same query carry a single control, at the top of the section they share.
 */
export function RefreshButton({
  refetch,
  label = 'Refresh',
  title = 'Refresh',
  className,
}: {
  /** The table's own refetch(es). Rejections are swallowed on purpose — the query's
   *  existing error surface renders the failure, and the button must re-enable (AC-12). */
  refetch: () => unknown;
  label?: string;
  /** The tooltip. AC-11 fixes the wording at 'Refresh'; the prop stays so a caller can
   *  say something more specific if one ever needs to. */
  title?: string;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  // A refresh that resolves after the table unmounted must not setState on a dead tree.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const press = useCallback(() => {
    if (busy) return;
    setBusy(true);
    Promise.resolve()
      .then(() => refetch())
      .catch(() => undefined)
      .finally(() => {
        if (alive.current) setBusy(false);
      });
  }, [busy, refetch]);

  // The accessible NAME changes with the state, so a screen reader that re-announces
  // the focused control on press says it is running rather than repeating 'Refresh'.
  const name = busy ? 'Refreshing…' : label;
  return (
    <Tooltip text={title}>
      {(describedBy) => (
        <button
          type="button"
          className={`${className ?? 'mini-btn mini-btn--icon'} refresh-btn`}
          data-testid="refresh-button"
          onClick={press}
          /* `aria-disabled`, NOT `disabled`. A disabled button is removed from the tab
             order and stops firing pointer events, so the moment it goes busy the
             tooltip explaining what it is disappears and keyboard focus is thrown to
             the next control — mid-press. The press itself is refused by the guard in
             `press()` instead, which is the same protection without the side effects. */
          aria-disabled={busy || undefined}
          aria-busy={busy}
          aria-label={name}
          aria-describedby={describedBy}
        >
          <Icon name="refresh" />
        </button>
      )}
    </Tooltip>
  );
}

/**
 * Sugar for the common case: a table fed by more than one query. Awaits them together
 * so the button stays busy until the LAST one lands — a control that re-enables while
 * half the table is still the old data is worse than none.
 */
export function refetchAll(...fns: Array<() => unknown>): () => Promise<unknown> {
  return () => Promise.all(fns.map((f) => Promise.resolve().then(f).catch(() => undefined)));
}
